import { DragEvent, ReactNode, useRef, useState } from 'react'

import { Text } from '@/components/base/text'
import { clx } from '@/utils/clx'

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'])
const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i

export const isAcceptedImageFile = (file: File) =>
	IMAGE_MIME_TYPES.has(file.type) || (file.type === '' && IMAGE_EXTENSIONS.test(file.name))

export const FileDropZone = ({
	onFiles,
	disabled,
	hint,
	className,
	children
}: {
	onFiles: (files: FileList) => void
	disabled?: boolean
	hint?: string
	className?: string
	children: ReactNode
}) => {
	const [isDragOver, setIsDragOver] = useState(false)
	const dragDepth = useRef(0)

	const resetDrag = () => {
		dragDepth.current = 0
		setIsDragOver(false)
	}

	const hasFiles = (event: DragEvent<HTMLDivElement>) => Array.from(event.dataTransfer.types).includes('Files')

	const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		if (disabled || !hasFiles(event)) return
		dragDepth.current += 1
		setIsDragOver(true)
	}

	const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		if (disabled || !hasFiles(event)) return
		event.dataTransfer.dropEffect = 'copy'
	}

	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		dragDepth.current = Math.max(0, dragDepth.current - 1)
		if (dragDepth.current === 0) setIsDragOver(false)
	}

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		resetDrag()
		if (disabled) return
		if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files)
	}

	return (
		<div
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			className={clx('relative', className)}
		>
			{children}
			{isDragOver && hint && (
				<div className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-dashed border-ui-border-interactive bg-ui-bg-base p-3 text-center'>
					<Text size='small' weight='plus'>
						{hint}
					</Text>
				</div>
			)}
		</div>
	)
}
